/**
 * v1 → domain conversion for production resourcing ingress.
 * Import only from `*.http.ts` modules.
 */

import type { Booking, Event, Expense, Production } from '__types__';
import { maybe } from '@passionware/monads';
import { bookingBillingFromV1 } from 'v5/api/booking/bookingBillingFromV1.http';
import {
  normalizeBookingForResourcing,
  normalizeExpenseForResourcing,
  normalizeProductionForResourcing
} from 'v5/api/resourcing/resourcingMoney.http';
import type { BillingAssignment } from 'v5/core/production-resourcing/billing-tree';
import {
  buildResourcingBillingTree,
  type ResourcingBillingExpenseSlotInput,
  type ResourcingBillingGroupInput,
  type ResourcingBillingResourceSlotInput,
  type ResourcingBillingTreeInput
} from 'v5/core/production-resourcing/buildResourcingBillingTree';
import type { ResourcingBillingTree } from 'v5/core/production-resourcing/billing-tree';
import type { ResourceSlotPlan } from 'v5/core/production-resourcing/billing-tree';
import { expenseBillingFromV1 } from 'v5/api/resourcing/expenseBillingFromV1.http';
import type { ExpenseLine } from 'v5/core/expense-billing/model';
type ResourceSlotV1 = NonNullable<Production['resource_slots']>[number] & {
  archived?: boolean;
  resource_slot_assignments?: Array<
    {
      id: number;
      booking_id?: number | null;
      contact_id?: number | null;
      events?: Event[] | null;
    } & Record<string, unknown>
  >;
};

type BudgetExpenseV1 = NonNullable<Production['budget_expenses']>[number] & {
  archived?: boolean;
  expenses?: Expense[];
};

/** v1 DELETE /expenses/:id archives rows; production embeds still return them until filtered. */
function activeExpenses(expenses: Expense[] | undefined): Expense[] {
  return (expenses ?? []).filter(expense => !expense.archived);
}

function slotPlanFromEstimateFields(
  fields: {
    estimate_rate_currency?: string | null;
    estimate_rate_amount?: number | null;
    estimate_rate_interval?: string | null;
    estimate_rate_quantity?: number | null;
    estimate_rate_agency_fee?: number | null;
    estimate_rate_payroll_fee?: number | null;
    estimate_rate_processing_fee?: number | null;
  },
  currency: string
): ResourceSlotPlan {
  const hasFees =
    fields.estimate_rate_agency_fee != null ||
    fields.estimate_rate_payroll_fee != null ||
    fields.estimate_rate_processing_fee != null;

  return {
    rate: {
      currency: fields.estimate_rate_currency ?? currency,
      amountPerUnit: fields.estimate_rate_amount ?? 0,
      interval:
        fields.estimate_rate_interval === 'HOURLY'
          ? 'hourly'
          : fields.estimate_rate_interval === 'FIXED'
            ? 'fixed'
            : 'daily',
      fees: hasFees
        ? maybe.of({
            agency: fields.estimate_rate_agency_fee ?? 0,
            payroll: fields.estimate_rate_payroll_fee ?? 0,
            processing: fields.estimate_rate_processing_fee ?? 0
          })
        : maybe.ofAbsent()
    },
    estimateQuantity: fields.estimate_rate_quantity ?? 1,
    estimatedExpenses: []
  };
}

export function mapV1ProductionToBillingTreeInput(
  production: Production,
  bookingsById: Record<number, Booking>,
  expensesByBookingId: Record<number, Expense[]>
): ResourcingBillingTreeInput {
  const normalized = normalizeProductionForResourcing(production);
  const currency = normalized.budget_currency ?? 'USD';
  const productionId = normalized.id ?? 0;

  const groups: ResourcingBillingGroupInput[] = (normalized.groups ?? []).map(
    g => ({
      id: g.id,
      name: g.name ?? '',
      order: g.order ?? 0,
      archived: g.archived
    })
  );

  const resourceSlots: ResourcingBillingResourceSlotInput[] = (
    (normalized.resource_slots ?? []) as ResourceSlotV1[]
  ).map(slot => {
    const assignments: BillingAssignment[] = [];
    for (const assignment of slot.resource_slot_assignments ?? []) {
      if (!assignment.booking_id) continue;
      const booking = bookingsById[assignment.booking_id];
      if (!booking) continue;
      const bookingExpenses = activeExpenses(
        expensesByBookingId[assignment.booking_id]
      ).map(e => expenseBillingFromV1.lineFromExpense(e, 'committed'));
      assignments.push({
        id: String(assignment.id),
        assignmentId: assignment.id,
        bookingId: String(assignment.booking_id),
        contactId: maybe.of(
          assignment.contact_id ?? booking.contact_id ?? null
        ),
        statusId: maybe.of(booking.status_id ?? null),
        includeInTotals: true,
        billing: bookingBillingFromV1.billingFromBookingFields(
          booking,
          assignment.events ?? [],
          bookingExpenses
        )
      });
    }

    return {
      id: slot.id,
      groupId: slot.group_id ?? 0,
      order: slot.order ?? 0,
      note: slot.note ?? null,
      slotCategoryId: slot.slot_category_id ?? null,
      includeInTotals: slot.include_in_calculation !== false,
      archived: slot.archived,
      plan:
        assignments.length === 0
          ? slotPlanFromEstimateFields(slot, currency)
          : undefined,
      assignments
    };
  });

  const expenseSlots: ResourcingBillingExpenseSlotInput[] = (
    (normalized.budget_expenses ?? []) as BudgetExpenseV1[]
  ).map(expenseSlot => {
    const lines = activeExpenses(expenseSlot.expenses).map(exp => ({
      id: String(exp.id),
      includeInTotals: expenseSlot.include_in_calculation !== false,
      line: expenseBillingFromV1.lineFromExpense(exp, 'committed')
    }));
    return {
      id: expenseSlot.id,
      groupId: expenseSlot.group_id ?? 0,
      order: expenseSlot.order ?? 0,
      note: expenseSlot.note ?? null,
      slotCategoryId: expenseSlot.slot_category_id ?? null,
      includeInTotals: expenseSlot.include_in_calculation !== false,
      archived: expenseSlot.archived,
      plan:
        lines.length === 0
          ? slotPlanFromEstimateFields(expenseSlot, currency)
          : undefined,
      lines
    };
  });

  return {
    productionId,
    presentationCurrency: currency,
    groups,
    resourceSlots,
    expenseSlots
  };
}

export function mapV1ProductionToBillingTree(
  production: Production,
  bookings: Booking[],
  expensesByBookingId: Record<number, Expense[]>
): ResourcingBillingTree {
  const bookingsById: Record<number, Booking> = {};
  for (const booking of bookings) {
    bookingsById[booking.id] = normalizeBookingForResourcing(booking);
  }
  const normalizedExpenses: Record<number, Expense[]> = {};
  for (const [bookingId, expenses] of Object.entries(expensesByBookingId)) {
    normalizedExpenses[Number(bookingId)] = activeExpenses(expenses).map(
      normalizeExpenseForResourcing
    );
  }
  const input = mapV1ProductionToBillingTreeInput(
    production,
    bookingsById,
    normalizedExpenses
  );
  return buildResourcingBillingTree(input);
}

export type ProductionResourcingV1CachePrime = {
  production: Production;
  bookingsById: Record<number, Booking>;
};

export function primePayloadFromV1(
  production: Production,
  bookings: Booking[]
): ProductionResourcingV1CachePrime {
  const bookingsById: Record<number, Booking> = {};
  for (const booking of bookings) {
    bookingsById[booking.id] = booking;
  }
  return {
    production,
    bookingsById
  };
}
